/**
 * Progressive enhancement for snippets/product-form.liquid (C-011): a) variant
 * switch via the Section Rendering API (option_values query, DOM-swapped,
 * focus restored), b) quantity stepper over the native number input with a
 * live price readout via CremapuraCart.formatMoney (shop.money_format, never
 * a hardcoded symbol), c) Ajax add-to-cart (idle/adding/justAdded states) via
 * window.CremapuraCart. Does not open/manage a cart drawer; only dispatches
 * cart:updated (assets/cart.js) for that. Requires assets/cart.js loaded first.
 * Also swaps `[data-swap-on-variant-change]` elements by id from the fetch.
 */
(function () {
  if (window.CremapuraProductForm) return;

  var PRICE_PLACEHOLDER = '%%PRICE%%';
  var COUNT_PLACEHOLDER = '%%COUNT%%';
  var SUCCESS_REVERT_MS = 2400;
  var MIN_QTY = 1;
  var MAX_QTY = 20;

  function formatMoney(cents, format) {
    return window.CremapuraCart ? window.CremapuraCart.formatMoney(cents, format) : String(cents);
  }

  function clampQuantity(value) {
    var qty = parseInt(value, 10);
    if (isNaN(qty)) qty = MIN_QTY;
    return Math.min(MAX_QTY, Math.max(MIN_QTY, qty));
  }

  function getWrapper(el) {
    return el && el.closest ? el.closest('[data-product-form]') : null;
  }

  function getMoneyFormat(wrapper) {
    return wrapper.getAttribute('data-money-format') || '${{amount}}';
  }

  function setSubmitLabelIdle(submitBtn, qty, moneyFormat) {
    var labelEl = submitBtn.querySelector('[data-product-form-submit-label]');
    if (!labelEl) return;
    var template = submitBtn.getAttribute('data-label-idle-template');
    var unitCents = parseInt(submitBtn.getAttribute('data-unit-price-cents'), 10);
    if (template && !isNaN(unitCents)) {
      labelEl.textContent = template.replace(PRICE_PLACEHOLDER, formatMoney(unitCents * qty, moneyFormat));
    }
  }

  function updatePriceDisplay(wrapper) {
    var qtyInput = wrapper.querySelector('[data-product-form-quantity]');
    if (!qtyInput) return;

    var qty = clampQuantity(qtyInput.value);
    var moneyFormat = getMoneyFormat(wrapper);

    var totalPriceEl = wrapper.querySelector('[data-product-form-total-price]');
    if (totalPriceEl) {
      var unitCents = parseInt(totalPriceEl.getAttribute('data-unit-price-cents'), 10);
      if (!isNaN(unitCents)) totalPriceEl.textContent = formatMoney(unitCents * qty, moneyFormat);
    }

    var singleNote = wrapper.querySelector('[data-product-form-price-note-single]');
    var multiNote = wrapper.querySelector('[data-product-form-price-note-multi]');
    if (singleNote && multiNote) {
      if (qty > 1) {
        var template = multiNote.getAttribute('data-template') || '';
        multiNote.textContent = template.replace(COUNT_PLACEHOLDER, String(qty));
        multiNote.hidden = false;
        singleNote.hidden = true;
      } else {
        multiNote.hidden = true;
        singleNote.hidden = false;
      }
    }

    var submitBtn = wrapper.querySelector('[data-product-form-submit]');
    if (submitBtn && submitBtn.dataset.state === 'idle') setSubmitLabelIdle(submitBtn, qty, moneyFormat);

    var liveRegion = wrapper.querySelector('[data-quantity-live]');
    if (liveRegion) liveRegion.textContent = String(qty);
  }

  function setSubmitState(submitBtn, state, wrapper) {
    submitBtn.dataset.state = state;
    var labelEl = submitBtn.querySelector('[data-product-form-submit-label]');

    if (state === 'adding') {
      submitBtn.disabled = true;
      if (labelEl) labelEl.textContent = submitBtn.getAttribute('data-label-adding') || '';
    } else if (state === 'justAdded') {
      submitBtn.disabled = false;
      if (labelEl) labelEl.textContent = submitBtn.getAttribute('data-label-success') || '';
    } else {
      submitBtn.disabled = false;
      var qtyInput = wrapper.querySelector('[data-product-form-quantity]');
      var qty = qtyInput ? clampQuantity(qtyInput.value) : MIN_QTY;
      setSubmitLabelIdle(submitBtn, qty, getMoneyFormat(wrapper));
    }
  }

  function init(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var wrappers = scope.matches && scope.matches('[data-product-form]')
      ? [scope]
      : scope.querySelectorAll('[data-product-form]');
    wrappers.forEach(updatePriceDisplay);
  }

  function syncQuantityButtons(wrapper) {
    var qtyInput = wrapper.querySelector('[data-product-form-quantity]');
    if (!qtyInput) return;
    var qty = clampQuantity(qtyInput.value);
    var minusBtn = wrapper.querySelector('[data-quantity-minus]');
    var plusBtn = wrapper.querySelector('[data-quantity-plus]');
    if (minusBtn) minusBtn.disabled = qty <= MIN_QTY;
    if (plusBtn) plusBtn.disabled = qty >= MAX_QTY;
  }

  function handleQuantityStep(button, direction) {
    var wrapper = getWrapper(button);
    if (!wrapper) return;
    var qtyInput = wrapper.querySelector('[data-product-form-quantity]');
    if (!qtyInput) return;
    qtyInput.value = clampQuantity(clampQuantity(qtyInput.value) + direction);
    updatePriceDisplay(wrapper);
    syncQuantityButtons(wrapper);
  }

  function dispatchClientError(source, message) {
    document.dispatchEvent(
      new CustomEvent('cart:error', {
        bubbles: true,
        detail: { source: source, status: null, description: message, message: message }
      })
    );
  }

  function handleOptionChange(radio) {
    var wrapper = getWrapper(radio);
    if (!wrapper) return;

    var sectionId = wrapper.getAttribute('data-section-id');
    if (!sectionId) return;

    var checkedInputs = wrapper.querySelectorAll('[data-product-form-option-input]:checked');
    if (!checkedInputs.length) return;

    var ids = [];
    checkedInputs.forEach(function (input) {
      ids.push(input.getAttribute('data-option-value-id'));
    });

    var focusId = radio.id;
    wrapper.setAttribute('aria-busy', 'true');

    var url = window.location.pathname +
      '?section_id=' + encodeURIComponent(sectionId) +
      '&option_values=' + ids.map(encodeURIComponent).join(',');

    fetch(url, { headers: { Accept: 'text/html' } })
      .then(function (response) {
        if (!response.ok) throw new Error('Section Rendering API request failed: ' + response.status);
        return response.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var newWrapper = doc.querySelector('[data-product-form][data-section-id="' + sectionId + '"]');
        if (!newWrapper) {
          wrapper.removeAttribute('aria-busy');
          return;
        }

        wrapper.replaceWith(newWrapper);
        updatePriceDisplay(newWrapper);
        syncQuantityButtons(newWrapper);

        document.querySelectorAll('[data-swap-on-variant-change]').forEach(function (el) {
          var fresh = el.id && doc.getElementById(el.id);
          if (fresh) el.replaceWith(fresh);
        });

        var focusEl = focusId ? document.getElementById(focusId) : null;
        if (focusEl) focusEl.focus();

        document.dispatchEvent(
          new CustomEvent('product-form:variant-changed', { bubbles: true, detail: { sectionId: sectionId } })
        );
      })
      .catch(function (error) {
        wrapper.removeAttribute('aria-busy');
        dispatchClientError('variant-change', (error && error.message) || 'Variant switch failed.');
      });
  }

  function handleSubmit(event) {
    var wrapper = getWrapper(event.target);
    if (!wrapper) return;

    var submitBtn = wrapper.querySelector('[data-product-form-submit]');
    if (!submitBtn || !window.CremapuraCart) return;

    event.preventDefault();

    var variantInput = wrapper.querySelector('[data-product-form-variant-id]');
    var qtyInput = wrapper.querySelector('[data-product-form-quantity]');
    var variantId = variantInput ? variantInput.value : null;
    var quantity = qtyInput ? clampQuantity(qtyInput.value) : MIN_QTY;
    if (!variantId) return;

    setSubmitState(submitBtn, 'adding', wrapper);

    window.CremapuraCart.addItem(variantId, quantity).then(function (result) {
      if (result) {
        setSubmitState(submitBtn, 'justAdded', wrapper);
        window.setTimeout(function () {
          setSubmitState(submitBtn, 'idle', wrapper);
        }, SUCCESS_REVERT_MS);
      } else {
        setSubmitState(submitBtn, 'idle', wrapper);
      }
    });
  }

  function setup() {
    document.addEventListener('change', function (event) {
      var target = event.target;
      if (target.matches && target.matches('[data-product-form-option-input]')) {
        handleOptionChange(target);
      } else if (target.matches && target.matches('[data-product-form-quantity]')) {
        var wrapper = getWrapper(target);
        if (!wrapper) return;
        target.value = clampQuantity(target.value);
        updatePriceDisplay(wrapper);
        syncQuantityButtons(wrapper);
      }
    });

    document.addEventListener('click', function (event) {
      var minusBtn = event.target.closest && event.target.closest('[data-quantity-minus]');
      var plusBtn = event.target.closest && event.target.closest('[data-quantity-plus]');
      if (minusBtn) handleQuantityStep(minusBtn, -1);
      if (plusBtn) handleQuantityStep(plusBtn, 1);
    });

    document.addEventListener('submit', function (event) {
      if (event.target.matches && event.target.matches('form[data-type="add-to-cart-form"]')) handleSubmit(event);
    }, true);

    init(document);
    document.querySelectorAll('[data-product-form]').forEach(syncQuantityButtons);

    document.addEventListener('shopify:section:load', function (event) {
      init(event.target);
      event.target.querySelectorAll &&
        event.target.querySelectorAll('[data-product-form]').forEach(syncQuantityButtons);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setup);
  } else {
    setup();
  }

  window.CremapuraProductForm = { formatMoney: formatMoney };
})();
